import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Checkbox } from '@webapp/common/components/checkbox';
import { CustomStylesCtx, useTextStyle } from '@webapp/common/components/custom-styles';
import { Scrollarea } from '@webapp/common/components/scrollarea';
import { CssPageBlock, CssQuestionType, CssUiComponent } from '@webapp/common/components/survey-custom';
import { removeTransparency } from '@webapp/common/lib/color';
import { setHtml } from '@webapp/common/lib/ui';
import { CANTRATE_ID } from '@webapp/common/resources/constants';
import { AnswerType, QuestionType } from '@webapp/common/resources/survey';

import type { QuestionAnswerActor } from '@webapp/survey/src/entities/answer-machine';
import type { GroupEvents, GroupMachineActor } from '@webapp/survey/src/machine/group-machine';
import { excludeHiddenAnswers } from '@webapp/survey/src/machine/helpers/logic';
import type { QuestionContext } from '@webapp/survey/src/machine/question-machine';
import { usePipingAnswersReplacer } from '@webapp/survey/src/machine/use-piping-answers-replacer';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import type { MatrixCell } from './matrix-cell';
import { useFixedHeader } from './use-fixed-header';
import { useMatrixParams } from './use-matrix-params';

import css from './matrix-layout.css';

const Spacer: FC<{
    height: string;
}> = ({ height }) => (
    <div className={cn(CssUiComponent.CELL, css.cell, css.span)} style={{ height }}>
        <div>&nbsp;</div>
    </div>
);

const handleCantRate = (checked: boolean, answer, groupsActors) => {
    const data = {
        type: 'CHANGE_RESPONSE_VALUE',
        value: checked ? CANTRATE_ID : '',
        answerId: answer.id,
    } as GroupEvents;

    if (
        answer.questionType === QuestionType.MATRIX_DROPDOWN_LIST ||
        answer.questionType === QuestionType.MATRIX_TEXT_ANSWER
    ) {
        groupsActors.forEach((actor) => {
            actor.send(data);
        });
    } else {
        groupsActors[0].send(data);
    }
};

const CantRate: FC<{ checked: boolean; answer: any; groupsActors: any; cantRateText: string; className?: string }> = ({
    checked,
    answer,
    groupsActors,
    cantRateText,
    className,
}) => {
    return (
        <div className={cn(css.cantRate, className)}>
            <Checkbox
                checked={checked}
                label={cantRateText}
                onChange={(checked) => handleCantRate(checked, answer, groupsActors)}
            />
        </div>
    );
};

const Labels: FC<{
    withScroll: boolean;
    height: string;
    rowsHeights: Array<number | string>;
    style?: CSSProperties;
    answers: Array<QuestionAnswerActor>;
    groupsActors: Array<GroupMachineActor>;
    setLabelRefs: (idx: number) => (el: HTMLElement) => void;
    cantRate: boolean;
    cantRateText: string;
}> = ({ answers, height, rowsHeights, groupsActors, setLabelRefs, style, withScroll, cantRate, cantRateText }) => {
    const { textStyle } = useTextStyle();
    const pipingAnswersReplacer = usePipingAnswersReplacer();
    const emptyHeaders = height === '0px';
    const [, updateRerenderHack] = useState(1);
    const questions = useStateSelector((s) => s.questions);
    return (
        <div
            style={style}
            className={cn(css.rowHeader, {
                [css.withScroll]: withScroll,
                [css.empty]: emptyHeaders,
            })}
        >
            <Spacer height={height} />
            {answers.map((answerRef, idx) => {
                const answer = answerRef.getSnapshot().context;
                const value = groupsActors[0]?.getSnapshot().context?.responses[answer.id]?.getSnapshot()
                    .context?.value;
                let label = answer.value[0];
                let forwardParentAnswer: QuestionAnswerActor | null = null;
                questions.forEach((q) => {
                    const founded = q.getSnapshot().context.answersActors.find((a) => {
                        return a.getSnapshot().context.id === answer.forwardParent;
                    });
                    if (founded) {
                        forwardParentAnswer = founded;
                    }
                });
                if (forwardParentAnswer && forwardParentAnswer.getSnapshot().context.type === AnswerType.SELF) {
                    const sub = () => updateRerenderHack((prev) => (prev + 1) % 10);
                    label = forwardParentAnswer
                        .getSnapshot()
                        .context.response?.getSnapshot()
                        .context?.extra?.toString();
                    forwardParentAnswer.subscribe(sub);
                }
                return (
                    <Fragment key={answer.id}>
                        <div
                            className={cn(CssUiComponent.CELL, css.cell, css.label)}
                            key={answer.id}
                            ref={setLabelRefs(idx)}
                            style={{
                                height: rowsHeights[idx],
                            }}
                        >
                            <div
                                className={CssUiComponent.LABEL}
                                style={textStyle}
                                {...setHtml(pipingAnswersReplacer(label))}
                            />
                        </div>
                        {cantRate && (
                            <CantRate
                                checked={value === CANTRATE_ID}
                                cantRateText={cantRateText}
                                groupsActors={groupsActors}
                                answer={answer}
                                className={css.cantRateHead}
                            />
                        )}
                    </Fragment>
                );
            })}
        </div>
    );
};

const MatrixGroupAnswer = ({
    answerActor,
    style,
    headerStyleStatic,
    cell: Cell,
    groupsActors,
    cellHeight,
    question,
    cantRate,
    cantRateText,
}: {
    answerActor: QuestionAnswerActor;
    style: React.CSSProperties;
    headerStyleStatic: React.CSSProperties;
    groupsActors: Array<GroupMachineActor>;
    cell: FC<MatrixCell>;
    cellHeight?: string;
    question: QuestionContext;
    cantRate: boolean;
    cantRateText: string;
}) => {
    const pipingAnswersReplacer = usePipingAnswersReplacer();
    const customStyles = useContext(CustomStylesCtx);
    const answer = useSelector(answerActor, (s) => s.context);
    const [forwardParentActor, setForwardParentActor] = useState<undefined | QuestionAnswerActor>();
    const forwardParentContext = useSelector(forwardParentActor, (s) => s?.context);

    const forwardParentValue = useSelector(forwardParentContext?.response, (s) => s?.context.extra);
    const questions = useStateSelector((s) => s.questions);

    useEffect(() => {
        if (!answer.forwardParent && !forwardParentActor) return;

        questions.forEach((q) => {
            const founded = q.getSnapshot().context.answersActors.find((a) => {
                return a.getSnapshot().context.id === answer.forwardParent;
            });
            if (founded) {
                setForwardParentActor(founded);
            }
        });
    }, [questions, answer.forwardParent]);

    const checkIsMaxFilled = () => {
        if (question.params?.maxLength) {
            const sumOfAnswers = {};

            groupsActors.forEach((groupActor) => {
                Object.entries(groupActor.getSnapshot().context.responses).forEach(([answerId, r]) => {
                    if (!sumOfAnswers[answerId]) {
                        sumOfAnswers[answerId] = 0;
                    }
                    sumOfAnswers[answerId] += r.getSnapshot().context.value ? 1 : 0;
                });
            });

            if (!sumOfAnswers[answer.id]) {
                sumOfAnswers[answer.id] = 0;
            }

            return sumOfAnswers[answer.id] >= question.params.maxLength;
        }

        return false;
    };

    return (
        <>
            <div
                key={answer.id}
                style={style}
                className={cn(CssPageBlock.ANSWER, css.row, {
                    [css.rowInvalid]: !answer.valid,
                })}
            >
                <div
                    style={headerStyleStatic}
                    className={cn(CssUiComponent.CELL, css.cell, css.rowHeaderMobile, css.label, css.answer)}
                    {...setHtml(
                        pipingAnswersReplacer(forwardParentValue ? forwardParentValue.toString() : answer.value[0]),
                    )}
                />
                {groupsActors.map((groupActor) => {
                    return (
                        <Cell
                            answer={answer}
                            customStyles={customStyles}
                            group={groupActor}
                            height={cellHeight}
                            key={groupActor.getSnapshot().context.id}
                            question={question}
                            disabled={
                                groupActor.getSnapshot().context?.responses[answer.id]?.getSnapshot().context?.value ===
                                CANTRATE_ID
                            }
                            maxFilled={checkIsMaxFilled()}
                            onChange={() => handleCantRate(false, answer, groupsActors)}
                        />
                    );
                })}
            </div>
            {cantRate && (
                <CantRate
                    checked={
                        groupsActors[0]?.getSnapshot().context?.responses[answer.id]?.getSnapshot().context?.value ===
                        CANTRATE_ID
                    }
                    className={css.cantRateAnswers}
                    cantRateText={cantRateText}
                    groupsActors={groupsActors}
                    answer={answer}
                />
            )}
        </>
    );
};
export const MatrixLayout: VFC<{
    className: string;
    question: QuestionContext;
    cell: FC<MatrixCell>;
}> = ({ cell: Cell, className, question }) => {
    const pipingAnswersReplacer = usePipingAnswersReplacer();
    const {
        answersActors: answersActorsRaw,
        groupsActors,
        params: { cantRate, cantRateText },
    } = question;
    const [, updateRerenderHack] = useState(1);

    useEffect(() => {
        const sub = () => updateRerenderHack((prev) => (prev + 1) % 10);
        const unsubs = answersActors.map((actor) => {
            return actor.subscribe(sub);
        });
        return () => {
            unsubs.forEach((unsub) => unsub.unsubscribe());
        };
    }, []);

    const answersActors = excludeHiddenAnswers(answersActorsRaw);
    const customStyles = useContext(CustomStylesCtx);
    const {
        matrix: { fixedHeader },
    } = customStyles;
    const { itemsGap, listStyle, textStyle } = useTextStyle();
    const headerStyle = useMemo(() => ({ ...textStyle, ...fixedHeader }), [fixedHeader, textStyle]);
    const [withScroll, setWithScroll] = useState(false);
    const { bgRef, headerRef, rootRef } = useFixedHeader({
        backgroundColor: removeTransparency(headerStyle.backgroundColor),
    });
    const contentRef = useRef();
    const { headerHeight, ready, rowsHeight, setLabelRefs } = useMatrixParams(answersActors, headerRef, contentRef);
    const headerStyleStatic = useMemo(() => ({ ...headerStyle, backgroundColor: undefined }), [headerStyle]);
    const gridStyle = useMemo(
        () => ({
            rowGap: itemsGap,
        }),
        [itemsGap],
    );
    const contentStyle = useMemo(
        () => ({ gridTemplateColumns: `repeat(${groupsActors.length}, 1fr)`, ...gridStyle }),
        [gridStyle, groupsActors.length],
    );

    const getAnswerStyle = useCallback(
        (idx: number) => ({
            height: `${rowsHeight[idx]}px`,
            ...listStyle,
        }),
        [rowsHeight, listStyle],
    );

    const onScrollResize = useCallback((scroll: any) => {
        const withScroll1 = scroll.withScroll();
        setWithScroll(withScroll1);
    }, []);

    return (
        <div className={cn(CssQuestionType.QUESTION, css.matrix, ready && css.ready, className)} ref={rootRef}>
            <Labels
                answers={answersActors}
                groupsActors={groupsActors}
                height={headerHeight}
                rowsHeights={rowsHeight}
                setLabelRefs={setLabelRefs}
                style={gridStyle}
                withScroll={withScroll}
                cantRate={cantRate}
                cantRateText={cantRateText}
            />
            <Scrollarea className={css.scrollarea} vertical={false} onResize={onScrollResize}>
                <div className={css.table} style={contentStyle}>
                    <div className={cn(css.row, css.headers)} ref={headerRef}>
                        {groupsActors.map((groupActor) => {
                            const { id, name } = groupActor.getSnapshot().context;

                            return (
                                <div
                                    className={cn(CssUiComponent.CELL, CssUiComponent.LABEL, css.cell, css.header)}
                                    key={id}
                                    style={headerStyleStatic}
                                    {...setHtml(pipingAnswersReplacer(name || ''))}
                                />
                            );
                        })}
                    </div>
                    <div className={css.answers} ref={contentRef}>
                        {answersActors.map((actor, idx) => (
                            <MatrixGroupAnswer
                                cellHeight={`${rowsHeight[idx]}px`}
                                key={actor.getSnapshot().context.id}
                                answerActor={actor}
                                style={getAnswerStyle(idx)}
                                headerStyleStatic={headerStyleStatic}
                                groupsActors={groupsActors}
                                cell={Cell}
                                question={question}
                                cantRate={cantRate}
                                cantRateText={cantRateText}
                            />
                        ))}
                    </div>
                </div>
            </Scrollarea>
            <div className={css.bg} ref={bgRef} />
        </div>
    );
};
MatrixLayout.displayName = 'MatrixLayout';
